import { useTranslation } from 'next-i18next';
import { path } from 'ramda';
import { ReactNode, useMemo } from 'react';
import { ISupplier } from '@bridebook/models/source/models/Suppliers.types';
import { Box } from '@bridebook/ui';
import InfoBlock from '@bridebook/web/app-shared/components/supplier/sections/info-block/info-block';
import LoggedOutTopSectionHeading from '@bridebook/web/app-shared/components/supplier/sections/logged-out-top-section-heading';
import { applyFormatter, isDefined } from '@bridebook/web/app-shared/lib/supplier/formatters';
import {
  accommodationItems,
  bridalSuiteValues,
  guestsItems,
  supplierTypeItems,
  supplierValues,
} from '@bridebook/web/app-shared/lib/supplier/data/venue-data-list';
import msg, { MsgKey } from '@bridebook/web/app-shared/lib/supplier/msg';
import InfoBlockV2 from '@bridebook/web/app-shared/components/supplier/sections/info-block-v2/info-block-v2';
import componentStyles from '@bridebook/web/app-basic/components/supplier/sections/features/data-list.style';
import { TSupplierFeaturesComponentVersion } from '@bridebook/web/app-basic/components/supplier/sections/features/features';
import { IconBedHeart, IconStageGuests, IconVenue2 } from '@bridebook/ui/src/icons/dynamic';

interface IProps {
  supplier: ISupplier | undefined;
  version?: TSupplierFeaturesComponentVersion;
}

type TInfoBlockType = 'supplierType' | 'guests' | 'accomodation';

export type TInfoBlockItem = {
  key: string;
  label: string;
  formattedValue?: string;
};

type TInfoBlockData = {
  title?: string;
  items: TInfoBlockItem[] | null;
  icon?: ReactNode;
};

const formatInfoBlockItems = (items: string[], supplier?: ISupplier): TInfoBlockItem[] | null => {
  if (!supplier) return null;
  return items
    .map((item) => {
      const itemPath = item === 'typeDetails.venue.type' ? 'typeDetails.venue' : item;
      const formattedValue = applyFormatter(itemPath, path(itemPath.split('.'))(supplier));

      return {
        key: item,
        label: msg[item as MsgKey]?.(),
        formattedValue: formattedValue ? formattedValue.toString() : undefined,
      };
    })
    .filter(({ formattedValue }) => isDefined(formattedValue));
};

const DataList: React.FC<IProps> = ({ supplier, version = 'v1' }) => {
  const { t } = useTranslation('supplier');

  const supplierName = supplier?.name;

  const infoBlocksData: Record<TInfoBlockType, TInfoBlockData | null> = useMemo(() => {
    const details = supplier?.typeDetails?.venue;
    const supplierType = details?.type?.[0];

    const showSupplierType =
      !supplierType || (supplierType === 'otherVenueType' && !details?.typeOther)
        ? false
        : supplierValues.includes(supplierType);

    const showGuests =
      guestsItems.filter((item) => Number(path(item.split('.'))(supplier))).length > 0;

    const bridalSuite = details?.accommodation?.bridalSuite;
    const showAccommodation =
      details?.accommodation?.available &&
      bridalSuite &&
      bridalSuiteValues.includes(bridalSuite) &&
      bridalSuite !== 'bridalSuiteNone';

    return {
      supplierType: showSupplierType
        ? {
            title: t('overview.venueType.title'),
            items:
              formatInfoBlockItems(supplierTypeItems, supplier)?.map((item) => ({
                ...item,
                label: item.formattedValue || '',
                formattedValue: undefined,
              })) || null,
            icon: <IconVenue2 color="indigoCrush" width={24} />,
          }
        : null,
      guests: showGuests
        ? {
            title: t('overview.guests.title'),
            items: formatInfoBlockItems(guestsItems, supplier),
            icon: <IconStageGuests color="indigoCrush" width={24} />,
          }
        : null,
      accomodation: showAccommodation
        ? {
            title: t('overview.accommodation.title'),
            items: formatInfoBlockItems(accommodationItems, supplier),
            icon: <IconBedHeart color="indigoCrush" width={24} />,
          }
        : null,
    };
  }, [supplier, t]);

  if (!Object.values(infoBlocksData).some(Boolean)) {
    return null;
  }

  const styles = componentStyles();
  return (
    <Box style={styles.wrapper}>
      <LoggedOutTopSectionHeading>
        {t('heading.overviewWithSupplierName', { supplierName })}
      </LoggedOutTopSectionHeading>

      {version === 'v1' && (
        <Box style={styles.infoBlocks}>
          {infoBlocksData.supplierType && (
            <Box style={styles.infoWrap}>
              <InfoBlock
                items={supplierTypeItems}
                icon={<IconVenue2 color="indigoCrush" width={24} />}
                supplier={supplier}
              />
            </Box>
          )}
          {infoBlocksData.guests && (
            <Box style={styles.infoWrap}>
              <InfoBlock
                items={guestsItems}
                icon={<IconStageGuests color="indigoCrush" width={24} />}
                supplier={supplier}
              />
            </Box>
          )}
          {infoBlocksData.accomodation && (
            <Box style={styles.infoWrap}>
              <InfoBlock
                items={accommodationItems}
                icon={<IconBedHeart color="indigoCrush" width={24} />}
                supplier={supplier}
              />
            </Box>
          )}
        </Box>
      )}
      {version === 'v2' && (
        <Box style={styles.infoBlocksV2}>
          {(() => {
            const entries = Object.entries(infoBlocksData).filter(([_key, value]) => !!value);
            const midIndex = Math.ceil(entries.length / 2);
            const firstColumnItems = entries.slice(0, midIndex);
            const secondColumnItems = entries.slice(midIndex);

            return (
              <>
                <Box style={styles.infoBlocksColumn}>
                  {firstColumnItems.map(([key, infoBlockData]) => (
                    <InfoBlockV2 key={`feature-items-box-${key}`} {...infoBlockData} />
                  ))}
                </Box>
                <Box style={styles.infoBlocksColumn}>
                  {secondColumnItems.map(([key, infoBlockData]) => (
                    <InfoBlockV2 key={`feature-items-box-${key}`} {...infoBlockData} />
                  ))}
                </Box>
              </>
            );
          })()}
        </Box>
      )}
    </Box>
  );
};

export default DataList;
